import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { COUNT_TIPS } from '../lib/adminCounts';
import InfoTip from '../components/InfoTip';
import { fetchAllRows } from '../../lib/fetchAll';

function StatCard({ label, value, accent, sub, loading, tip }) {
  return (
    <div style={{
      background: 'var(--brand-white)', border: '1px solid var(--brand-sand)',
      borderTop: `3px solid ${accent}`, borderRadius: 'var(--radius-lg)', padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--neutral-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</span>
        {tip && <InfoTip text={tip} />}
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -1, lineHeight: 1 }}>
        {loading ? '—' : (typeof value === 'number' ? value.toLocaleString() : (value || '—'))}
      </div>
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, marginTop: 5, color: 'var(--neutral-500)' }}>{sub}</div>}
    </div>
  );
}

function BarRow({ label, value, max, color = '#d97706', last }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-10)',
      padding: '9px 0', borderBottom: last ? 'none' : '1px solid var(--brand-cream)',
    }}>
      <div style={{
        fontSize: 12.5, color: 'var(--neutral-600)', width: 140, flexShrink: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 7, background: 'var(--brand-cream)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: color, borderRadius: 'var(--radius-sm)',
          transition: 'width 0.6s ease',
        }} />
      </div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--neutral-500)', width: 36, textAlign: 'right', flexShrink: 0 }}>
        {value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
      </div>
    </div>
  );
}

function WeekRow({ label, value, max, last }) {
  return <BarRow label={label} value={value} max={max} color="#9333ea" last={last} />;
}

function GenreTile({ emoji, name, pct }) {
  return (
    <div style={{
      background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)',
      padding: 'var(--space-14)', textAlign: 'center',
    }}>
      <div style={{ fontSize: 'var(--font-size-h4)', marginBottom: 'var(--space-6)' }}>{emoji}</div>
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-xxs)' }}>{name}</div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>{pct}% of libraries</div>
    </div>
  );
}

function SourceBar({ label, pct, color, last }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-md)',
      padding: '9px 0', borderBottom: last ? 'none' : '1px solid var(--brand-cream)',
    }}>
      <div style={{ fontSize: 12.5, color: 'var(--neutral-600)', width: 80, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 7, background: 'var(--brand-cream)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 'var(--radius-sm)', transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--neutral-500)', width: 36, textAlign: 'right' }}>{pct}%</div>
    </div>
  );
}

const GENRE_EMOJIS = {
  'Fantasy': '🧙', 'Romance': '💕', 'Thriller': '🔪', 'Mystery': '🔍',
  'Romantasy': '🐉', 'Contemporary': '☕', 'Horror': '👻', 'Sci-Fi': '🚀',
  'Historical': '📜', 'Literary Fiction': '📖', 'Young Adult': '🌸',
  'Dark Romance': '🖤', 'Paranormal': '🌙', 'Cozy': '🧸',
};

const TAG_SOURCES_META = [
  { label: 'Author', desc: 'Submitted directly by the author via Author Portal. Highest confidence.', color: 'var(--feedback-warning)' },
  { label: 'API',    desc: 'Pulled from Google Books API. Lowest confidence, unverified.',           color: '#2563eb' },
  { label: 'Reader', desc: 'Added or confirmed by a reader. Higher confidence than API.',             color: '#16a34a' },
];

export default function AnalyticsSection({ isMobile }) {
  const [loading, setLoading]         = useState(true);
  const [stats, setStats]             = useState({ dau: 0, avgBooks: 0, booksAdded: 0, postsThisWeek: 0 });
  const [topTropes, setTopTropes]     = useState([]);
  const [signupsByWeek, setSignupsByWeek] = useState([]);
  const [topGenres, setTopGenres]     = useState([]);
  const [tagSources, setTagSources]   = useState({ author: 0, api: 0, reader: 0 });
  const [totalUsers, setTotalUsers]   = useState(0);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const now           = new Date();
      const yesterday     = new Date(now - 24 * 60 * 60 * 1000).toISOString();
      const thisMonth     = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const eightWeeksAgo = new Date(now - 56 * 24 * 60 * 60 * 1000).toISOString();

      // Ranked lists need every row, so they page through the table rather
      // than reading one capped page. The tag source split is three numbers,
      // so it is three exact counts instead — book_tags.source is constrained
      // to exactly author / api / reader, so nothing falls outside them.
      const [
        dauRows,
        { count: totalUsersCount },
        { count: booksThisMonth },
        { count: postsThisWeek },
        tropeData,
        signupData,
        genrePrefs,
        { count: authorTags },
        { count: apiTags },
        { count: readerTags },
        { count: libraryCount },
      ] = await Promise.all([
        // Distinct people, not posts. This was
        // .select('user_id', { count: 'exact', head: true }), which counts
        // ROWS — naming a column in select() does not make the count
        // distinct — so one reader posting five times read as five "active
        // users". The rows are needed to dedupe, so this is paged.
        fetchAllRows(() => supabase.from('posts').select('user_id').gte('created_at', yesterday)),
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }),
        supabase.from('books').select('*', { count: 'exact', head: true }).gte('created_at', thisMonth).eq('status', 'live'),
        supabase.from('posts').select('*', { count: 'exact', head: true }).gte('created_at', new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString()),
        fetchAllRows(() => supabase.from('book_tags').select('id, trope_id, tropes(canonical_name)').not('trope_id', 'is', null)),
        fetchAllRows(() => supabase.from('user_profiles').select('id, created_at').gte('created_at', eightWeeksAgo)),
        fetchAllRows(() => supabase.from('user_preferences').select('id, preference_value').eq('preference_type', 'genre')),
        supabase.from('book_tags').select('*', { count: 'exact', head: true }).eq('source', 'author'),
        supabase.from('book_tags').select('*', { count: 'exact', head: true }).eq('source', 'api'),
        supabase.from('book_tags').select('*', { count: 'exact', head: true }).eq('source', 'reader'),
        supabase.from('user_library').select('*', { count: 'exact', head: true }),
      ]);

      // Top tropes
      const tropeCount = {};
      (tropeData || []).forEach(bt => {
        const name = bt.tropes?.canonical_name;
        if (name) tropeCount[name] = (tropeCount[name] || 0) + 1;
      });
      const sortedTropes = Object.entries(tropeCount).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, count]) => ({ name, count }));

      // Signups by week
      const weekBuckets = {};
      (signupData || []).forEach(row => {
        const d    = new Date(row.created_at);
        const week = new Date(d - d.getDay() * 86400000);
        const key  = week.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        weekBuckets[key] = (weekBuckets[key] || 0) + 1;
      });
      const weekEntries = Object.entries(weekBuckets).slice(-5).reverse().map(([label, count]) => ({ label, count }));

      // Top genres
      const genreCount = {};
      (genrePrefs || []).forEach(p => { const g = p.preference_value; if (g) genreCount[g] = (genreCount[g] || 0) + 1; });
      const totalGenrePicks = Object.values(genreCount).reduce((a, b) => a + b, 0);
      const sortedGenres = Object.entries(genreCount).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([name, count]) => ({
        name, emoji: GENRE_EMOJIS[name] || '📚',
        pct: totalGenrePicks > 0 ? Math.round((count / totalGenrePicks) * 100) : 0,
      }));

      // Tag sources — counted in the database, over every row
      const sourceCount = {
        author: authorTags || 0,
        api:    apiTags    || 0,
        reader: readerTags || 0,
      };
      const totalTags = sourceCount.author + sourceCount.api + sourceCount.reader || 1;
      const tagPcts = {
        author: Math.round((sourceCount.author / totalTags) * 100),
        api:    Math.round((sourceCount.api    / totalTags) * 100),
        reader: Math.round((sourceCount.reader / totalTags) * 100),
      };

      const avgBooks = totalUsersCount > 0 ? Math.round((libraryCount || 0) / totalUsersCount) : 0;

      const dauCount = new Set((dauRows || []).map(r => r.user_id).filter(Boolean)).size;
      setStats({ dau: dauCount, avgBooks, booksAdded: booksThisMonth || 0, postsThisWeek: postsThisWeek || 0 });
      setTopTropes(sortedTropes);
      setSignupsByWeek(weekEntries);
      setTopGenres(sortedGenres);
      setTagSources(tagPcts);
      setTotalUsers(totalUsersCount || 0);
    } catch (err) {
      console.error('AnalyticsSection load error:', err.message);
    }
    setLoading(false);
  }

  const tropeMax = topTropes[0]?.count || 1;
  const weekMax  = Math.max(...signupsByWeek.map(w => w.count), 1);

  const card = { background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '16px' : '20px 22px' };
  const cardTitle = { fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)', marginBottom: 'var(--space-14)' };

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>

      {/* Header */}
      <div style={{ marginBottom: isMobile ? 18 : 26 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, margin: 'var(--space-none)' }}>
          Analytics
        </h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 'var(--space-xs)', marginBottom: 'var(--space-none)' }}>
          Platform health and reading trends · {totalUsers.toLocaleString()} registered readers
        </p>
      </div>

      {/* Stats — 2×2 on mobile, 4×1 on desktop */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 10 : 14, marginBottom: isMobile ? 14 : 22 }}>
        <StatCard label="Active today"         value={stats.dau}           accent="#d97706" sub="people who posted, last 24h" loading={loading} tip={COUNT_TIPS.activeToday} />
        <StatCard label="Avg books / reader"   value={stats.avgBooks}      accent="#292524" sub="across all libraries"  loading={loading} tip={'Total saved books divided by the number of reader accounts, rounded.\n\nReaders with an empty library are included in the divisor, so this is the average across everyone, not across active readers.'} />
        <StatCard label="Books added"          value={stats.booksAdded}    accent="#16a34a" sub="live this month"       loading={loading} tip={'Books that went live this calendar month.\n\nExcludes books still awaiting review and books that were removed.'} />
        <StatCard label="Posts this week"      value={stats.postsThisWeek} accent="#9333ea" sub="community engagement"  loading={loading} tip={'Community posts created in the last 7 days.\n\nCounts posts, not people; see "Active today" for people.'} />
      </div>

      {/* Tropes + Signups — stacked on mobile, side-by-side on desktop */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 12 : 16, marginBottom: isMobile ? 12 : 16 }}>

        {/* Top tropes */}
        <div style={card}>
          <div style={cardTitle}>Top tropes this week</div>
          {loading ? (
            <div style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>
          ) : !topTropes.length ? (
            <div style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>No trope data yet</div>
          ) : (
            topTropes.map((t, i) => (
              <BarRow key={t.name} label={t.name} value={t.count} max={tropeMax} last={i === topTropes.length - 1} />
            ))
          )}
        </div>

        {/* Signups by week */}
        <div style={card}>
          <div style={cardTitle}>Signups by week</div>
          {loading ? (
            <div style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>
          ) : !signupsByWeek.length ? (
            <div style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>No signup data yet</div>
          ) : (
            signupsByWeek.map((w, i) => (
              <WeekRow key={w.label} label={`Week of ${w.label}`} value={w.count} max={weekMax} last={i === signupsByWeek.length - 1} />
            ))
          )}
        </div>
      </div>

      {/* Top genres — 2×2 on mobile, 4×1 on desktop */}
      <div style={{ ...card, marginBottom: isMobile ? 12 : 16 }}>
        <div style={cardTitle}>Top genres in user preferences</div>
        {loading ? (
          <div style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>
        ) : !topGenres.length ? (
          <div style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>No genre preference data yet</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 'var(--space-10)' }}>
            {topGenres.map(g => <GenreTile key={g.name} emoji={g.emoji} name={g.name} pct={g.pct} />)}
          </div>
        )}
      </div>

      {/* Tag source breakdown */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: 'var(--space-xs)', marginBottom: 'var(--space-14)' }}>
          <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--neutral-800)' }}>Tag source breakdown</div>
          <span style={{ fontSize: 11.5, color: 'var(--neutral-400)' }}>across all books in catalog</span>
        </div>

        {loading ? (
          <div style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading...</div>
        ) : (
          <>
            <SourceBar label="Author" pct={tagSources.author} color="#d97706" />
            <SourceBar label="API"    pct={tagSources.api}    color="#2563eb" />
            <SourceBar label="Reader" pct={tagSources.reader} color="#16a34a" last />

            {/* Legend — stacked on mobile, row on desktop */}
            <div style={{
              marginTop: 'var(--space-14)', padding: '10px 14px',
              background: 'var(--brand-paper)', border: '1px solid var(--brand-cream)', borderRadius: 'var(--radius-md)',
              display: 'flex', flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 12 : 24,
            }}>
              {TAG_SOURCES_META.map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)', flex: 1 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 'var(--radius-circle)', background: s.color, marginTop: 'var(--space-xs)', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--neutral-800)' }}>{s.label}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', lineHeight: 1.4 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}